import { useBox } from "@react-three/cannon";
import { MEZZANINE_Y, ROOF_HEIGHT } from "../../../lib/consts";

const WALL_HEIGHT = ROOF_HEIGHT - MEZZANINE_Y; // 13 - 7 = 6
const WALL_T = 0.4;

const LEFT_X = -4;
const RIGHT_X = 4;
const FRONT_Z = -10;
const BACK_Z = -20;

const WALL_BASE_Y = MEZZANINE_Y;

// Elevator door opening dimensions — must match Elevator.tsx constants
const ELEVATOR_D      = 2.0;    // elevator depth (Z span): -14 to -12
const ELEVATOR_DOOR_H = 3.2;    // door panel height
const ELEVATOR_CZ     = -13;    // world Z centre of elevator

// Hole extents in Z
const HOLE_NORTH = ELEVATOR_CZ + ELEVATOR_D / 2;  // -12
const HOLE_SOUTH = ELEVATOR_CZ - ELEVATOR_D / 2;  // -14

// Elevator opening: X from 2 to 4, Z from -12 to -14
const ELEVATOR_LEFT_X = 2;

function LeftWall() {
	const len = Math.abs(FRONT_Z - BACK_Z);
	const cz = (FRONT_Z + BACK_Z) / 2;

	const [ref] = useBox(() => ({
		type: "Static" as const,
		args: [WALL_T, WALL_HEIGHT, len],
		position: [LEFT_X, WALL_BASE_Y + WALL_HEIGHT / 2, cz],
	}));

	return (
		<>
			<mesh ref={ref} visible={false} />
			<mesh position={[LEFT_X, WALL_BASE_Y + WALL_HEIGHT / 2, cz]} castShadow receiveShadow>
				<boxGeometry args={[WALL_T, WALL_HEIGHT, len]} />
				<meshStandardMaterial color="#c2d5d8" roughness={0.9} />
			</mesh>
		</>
	);
}

function RightWall() {
	// Right wall with hole for elevator opening
	// Front segment: Z from FRONT_Z (-10) to HOLE_NORTH (-12)
	const frontLen = Math.abs(HOLE_NORTH - FRONT_Z); // 2
	const frontCz = (FRONT_Z + HOLE_NORTH) / 2; // -11

	// Back segment: Z from HOLE_SOUTH (-14) to BACK_Z (-20)
	const backLen = Math.abs(BACK_Z - HOLE_SOUTH); // 6
	const backCz = (HOLE_SOUTH + BACK_Z) / 2; // -17

	// ── Header above the elevator door opening ──
	//   Spans the hole in Z, from ELEVATOR_DOOR_H up to WALL_HEIGHT
	const headerH  = WALL_HEIGHT - ELEVATOR_DOOR_H;            // 2.8
	const headerCY = WALL_BASE_Y + ELEVATOR_DOOR_H + headerH / 2;  // 7 + 3.2 + 1.4 = 11.6
	const holeCZ   = (HOLE_NORTH + HOLE_SOUTH) / 2;            // -13

	const [refFront] = useBox(() => ({
		type: "Static" as const,
		args: [WALL_T, WALL_HEIGHT, frontLen],
		position: [RIGHT_X, WALL_BASE_Y + WALL_HEIGHT / 2, frontCz],
	}));

	const [refBack] = useBox(() => ({
		type: "Static" as const,
		args: [WALL_T, WALL_HEIGHT, backLen],
		position: [RIGHT_X, WALL_BASE_Y + WALL_HEIGHT / 2, backCz],
	}));

	const [refHeader] = useBox(() => ({
		type: "Static" as const,
		args: [WALL_T, headerH, ELEVATOR_D],
		position: [RIGHT_X, headerCY, holeCZ],
	}));

	return (
		<>
			<mesh ref={refFront} visible={false} />
			<mesh position={[RIGHT_X, WALL_BASE_Y + WALL_HEIGHT / 2, frontCz]} castShadow receiveShadow>
				<boxGeometry args={[WALL_T, WALL_HEIGHT, frontLen]} />
				<meshStandardMaterial color="#c2d5d8" roughness={0.9} />
			</mesh>
			<mesh ref={refBack} visible={false} />
			<mesh position={[RIGHT_X, WALL_BASE_Y + WALL_HEIGHT / 2, backCz]} castShadow receiveShadow>
				<boxGeometry args={[WALL_T, WALL_HEIGHT, backLen]} />
				<meshStandardMaterial color="#c2d5d8" roughness={0.9} />
			</mesh>
			{/* Header above door opening (closes gap between door top and ceiling) */}
			<mesh ref={refHeader} visible={false} />
			<mesh position={[RIGHT_X, headerCY, holeCZ]} castShadow receiveShadow>
				<boxGeometry args={[WALL_T, headerH, ELEVATOR_D]} />
				<meshStandardMaterial color="#c2d5d8" roughness={0.9} />
			</mesh>
		</>
	);
}

function FrontWall() {
	// Front wall: full width from LEFT_X to RIGHT_X
	const spanX = Math.abs(RIGHT_X - LEFT_X) + WALL_T;
	const cx = (LEFT_X + RIGHT_X) / 2;

	const [ref] = useBox(() => ({
		type: "Static" as const,
		args: [spanX, WALL_HEIGHT, WALL_T],
		position: [cx, WALL_BASE_Y + WALL_HEIGHT / 2, FRONT_Z],
	}));

	return (
		<>
			<mesh ref={ref} visible={false} />
			<mesh position={[cx, WALL_BASE_Y + WALL_HEIGHT / 2, FRONT_Z]} castShadow receiveShadow>
				<boxGeometry args={[spanX, WALL_HEIGHT, WALL_T]} />
				<meshStandardMaterial color="#c2d5d8" roughness={0.9} />
			</mesh>
		</>
	);
}

function BackWall() {
	// Back wall with hole for elevator opening
	// Left segment: X from LEFT_X (-4) to ELEVATOR_LEFT_X (2)
	const leftLen = Math.abs(ELEVATOR_LEFT_X - LEFT_X); // 6
	const leftCx = (LEFT_X + ELEVATOR_LEFT_X) / 2; // -1

	// Right segment: X from ELEVATOR_RIGHT_X (4) to RIGHT_X (4)
	// No right segment needed since elevator goes to the right wall

	const [refLeft] = useBox(() => ({
		type: "Static" as const,
		args: [leftLen, WALL_HEIGHT, WALL_T],
		position: [leftCx, WALL_BASE_Y + WALL_HEIGHT / 2, BACK_Z],
	}));

	return (
		<>
			<mesh ref={refLeft} visible={false} />
			<mesh position={[leftCx, WALL_BASE_Y + WALL_HEIGHT / 2, BACK_Z]} castShadow receiveShadow>
				<boxGeometry args={[leftLen, WALL_HEIGHT, WALL_T]} />
				<meshStandardMaterial color="#c2d5d8" roughness={0.9} />
			</mesh>
		</>
	);
}

function Room2Floor() {
	const w = RIGHT_X - LEFT_X;
	const d = FRONT_Z - BACK_Z; // front is -10, back is -20 => 10
	const cx = (LEFT_X + RIGHT_X) / 2;
	const cz = (FRONT_Z + BACK_Z) / 2;

	const [ref] = useBox(() => ({
		type: "Static" as const,
		args: [w, 0.1, d],
		position: [cx, WALL_BASE_Y, cz],
	}));

	return (
		<>
			<mesh ref={ref} visible={false} />
			<mesh position={[cx, WALL_BASE_Y, cz]} receiveShadow>
				<boxGeometry args={[w, 0.1, d]} />
				<meshStandardMaterial color="#8a7362" roughness={0.8} />
			</mesh>
		</>
	);
}

export default function RoomEnclosure2() {
	return (
		<>
			<Room2Floor />
			<LeftWall />
			<RightWall />
			<FrontWall />
			<BackWall />
		</>
	);
}
